import {calculateAvailableMovementCells} from '../gameState/calculateAvailableMovementCells';
import {HEX_DIRECTIONS} from '../../constants/geometry';
import {checkIfUnitTypeIsFlyingForFaction} from '../../configs/factions';

const toStep = (hexKey) => {
  const [q, r] = String(hexKey).split(',').map(Number);
  return {q, r};
};

const checkIfNeighbourHexes = (fromHexKey, toHexKey) => {
  const from = toStep(fromHexKey);
  const to = toStep(toHexKey);

  return HEX_DIRECTIONS.some(([dq, dr]) => from.q + dq === to.q && from.r + dr === to.r);
};

/**
 * Путь отряда по ячейкам для анимации ЧУЖОГО перемещения — хода соперника по
 * сети и воспроизведения записи матча.
 *
 * Маршрут в экшене не передается: MOVE_UNIT несет только отряд и целевую
 * ячейку (см. redux/actions). Восстанавливаем его теми же правилами, по
 * которым ходит сам игрок — calculateAvailableMovementCells по состоянию ДО
 * хода (отряд еще на исходной ячейке). Так отряд соперника огибает горы, воду
 * и чужие отряды, а не режет поле по прямой: скорость у конницы 4 ячейки, и
 * прямая линия прошла бы сквозь непроходимое.
 *
 * Прямой линией ведем только на СОСЕДНЮЮ ячейку: там она и есть настоящий
 * путь. Так же устроен маневр — это всегда один шаг (см.
 * GameScene.handleManeuverPhaseClick) — и полет.
 *
 * Не удалось восстановить маршрут (состояние разошлось с записью сюжетной
 * миссии, ход по правилам, которых уже нет) — возвращаем null: отряд
 * переставится мгновенно, как было до этой анимации. Прямая линия через
 * несколько ячеек провела бы отряд сквозь горы, море и чужие отряды — это
 * заметнее и хуже мгновенной перестановки.
 *
 * @param {Object} params
 * @param {Object} params.hexGridRenderer — из него берется только наличие ячеек
 * @param {Object} params.hexes — жетоны ячеек (проходимость)
 * @param {Array} params.players
 * @param {Array} params.prevUnits — отряды ДО перемещения
 * @param {string} params.unitId
 * @param {string} params.toHexKey — куда отряд встал
 * @param {boolean} [params.isManeuver]
 * @param {Array<string>} [params.noStopHexKeys]
 * @returns {Array<{q: number, r: number}>|null} null — анимировать нечего
 */
export const resolveUnitMovementPath = ({
  hexGridRenderer,
  hexes,
  players,
  prevUnits,
  unitId,
  toHexKey,
  isManeuver = false,
  noStopHexKeys = [],
} = {}) => {
  const unitBeforeMove = (prevUnits || []).find((unit) => unit.id === unitId);
  const fromHexKey = unitBeforeMove?.hexKey;
  // Отряда до действия не было (появился на поле) или он уже стоял на целевой
  // ячейке — вести по полю нечего
  if (!fromHexKey || !toHexKey || fromHexKey === toHexKey) {
    return null;
  }

  // Летающий отряд летит по прямой над всем, что на пути: маршрут ему не нужен
  if (
    isManeuver
    || checkIfNeighbourHexes(fromHexKey, toHexKey)
    || checkIfUnitTypeIsFlyingForFaction(unitBeforeMove.type, unitBeforeMove.factionId)
  ) {
    return [toStep(fromHexKey), toStep(toHexKey)];
  }

  try {
    const {paths} = calculateAvailableMovementCells(
      hexGridRenderer, hexes, prevUnits, unitBeforeMove, players, noStopHexKeys
    );
    const path = paths?.[toHexKey];

    return path && path.length >= 2 ? path : null;
  } catch (error) {
    console.warn('[UnitMovement] не удалось восстановить путь отряда', error);
    return null;
  }
};
